import type React from 'react';
import { forwardRef, isValidElement, useId, useState } from 'react';
import { Controller } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { Button, cn } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { RichTextEditor } from '../RTE';
import type {
  ControlledTextAreaFormFieldProps,
  ControlledTextFormFieldProps,
  TextFormFieldProps,
} from 'types/common/form-fields';
import {
  CustomField,
  domProps,
  FieldLabel,
  FieldMessage,
  FormHelpToolTip,
  InputContainer,
  MuteLabel,
  RequiredMark,
} from './chrome';
import { FIELD_INPUT, FIELD_INPUT_INVALID, FIELD_TEXTAREA } from './styles';

/** A string label doubles as the placeholder when none is given. */
function labelPlaceholder(label: unknown): string | undefined {
  return typeof label === 'string' && !isValidElement(label)
    ? label
    : undefined;
}

export function inputClass(error: unknown, ...extra: (string | undefined)[]) {
  // Callers' margins never reached the input: its reset beat Bootstrap's.
  return cn(FIELD_INPUT, !!error && FIELD_INPUT_INVALID, ...extra, 'sk:m-0!');
}

export const TextFormField = forwardRef(
  (
    {
      required,
      error,
      inline,
      inputRight,
      label,
      hint,
      width,
      placeholder,
      fieldClass,
      labelError,
      className,
      ...rest
    }: TextFormFieldProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const id = useId();
    return (
      <CustomField
        width={width}
        inline={inline || false}
        className={fieldClass}
      >
        <div className="sk:flex sk:items-baseline sk:justify-between">
          {label && (
            <MuteLabel
              htmlFor={id}
              className="sk:mb-1 sk:flex! sk:items-center"
            >
              {label}
              {required ? <RequiredMark /> : null}
            </MuteLabel>
          )}
          {labelError && (
            <span className="sk:text-xs sk:text-danger">
              {labelError?.message}
            </span>
          )}
        </div>
        <InputContainer inline={inline}>
          <input
            id={id}
            type="text"
            ref={ref}
            aria-invalid={!!error || undefined}
            placeholder={placeholder ?? labelPlaceholder(label)}
            {...domProps(rest)}
            className={inputClass(error, className)}
          />
          {inputRight}
        </InputContainer>
        {hint && <FieldMessage>{hint}</FieldMessage>}
      </CustomField>
    );
  },
);

export function ControlledTextFormField({
  control,
  name,
  defaultValue = '',
  required,
  error,
  inline,
  inputRight,
  label,
  hint,
  width,
  placeholder,
  fieldClass,
  inputContainerClass,
  onChange,
  helpText,
  helpPosition,
  hintTitleCase,
  onBlur,
  maxLength,
  showCharacterCount = false,
  ...rest
}: ControlledTextFormFieldProps) {
  const id = useId();
  const { className, ...inputProps } = rest;
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField
          onBlur={onBlur}
          width={width}
          inline={inline || false}
          className={fieldClass}
        >
          <FieldLabel
            label={label}
            required={required}
            helpText={helpText}
            helpPosition={helpPosition}
            htmlFor={id}
          />
          <InputContainer inline={inline} className={inputContainerClass}>
            <input
              id={id}
              type="text"
              data-field-value={name}
              aria-invalid={!!error || undefined}
              placeholder={placeholder ?? labelPlaceholder(label)}
              onChange={e => {
                let trimmedValue = e.target.value;
                if (trimmedValue?.trimStart) {
                  trimmedValue = trimmedValue.trimStart();
                  trimmedValue = trimmedValue.replace(/ {2}$/g, ' ');
                }
                controlOnChange(trimmedValue);
                if (onChange) {
                  onChange(trimmedValue);
                }
              }}
              value={value}
              maxLength={maxLength}
              {...domProps(inputProps)}
              className={inputClass(error, className)}
            />
            {showCharacterCount && maxLength !== undefined && (
              <CharacterCount>
                {String(value ?? '').length}/{maxLength}
              </CharacterCount>
            )}
            {inputRight}
          </InputContainer>
          {hint && (
            <FieldMessage>
              {hintTitleCase && typeof hint === 'string'
                ? CommonUtility.toCapitalize(hint)
                : hint}
            </FieldMessage>
          )}
        </CustomField>
      )}
    />
  );
}

/* Sits over the input's right end, on the label's line. */
function CharacterCount({ children }: { children: React.ReactNode }) {
  return (
    <span className="sk:pointer-events-none sk:absolute sk:-top-5 sk:right-2 sk:text-xs sk:text-fg-tertiary">
      {children}
    </span>
  );
}

const NO_SPECIAL_CHARACTERS = /[^\w\s\\-]/gi;

export function NoSpecialCharactedInput({
  control,
  name,
  defaultValue,
  required,
  error,
  inline,
  inputRight,
  maxLength,
  inputLeft,
  label,
  hint,
  width,
  placeholder,
  fieldClass,
  onChange,
  inputContainerClass,
  ...rest
}) {
  const id = useId();
  const [currentText, setCurrentText] = useState(0);
  const { className, ...inputProps } = rest;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField
          width={width}
          inline={inline || false}
          className={fieldClass}
        >
          <FieldLabel label={label} required={required} htmlFor={id} />
          <InputContainer inline={inline} className={inputContainerClass}>
            {inputLeft}
            <input
              id={id}
              type="text"
              maxLength={maxLength}
              data-field-value={name}
              aria-invalid={!!error || undefined}
              placeholder={placeholder || (isValidElement(label) ? '' : label)}
              onChange={e => {
                const cleaned = e.target.value.replace(
                  NO_SPECIAL_CHARACTERS,
                  '',
                );
                controlOnChange(cleaned);
                setCurrentText(cleaned.length);
                if (onChange) {
                  onChange(cleaned);
                }
              }}
              value={value}
              {...domProps(inputProps)}
              className={inputClass(error, 'sk:pr-8!', className)}
            />
            <span className="sk:absolute sk:right-2.5 sk:flex">
              <FormHelpToolTip
                leftMargin={false}
                helpText="No special characters allowed (e.g. #, %, @)"
              />
            </span>
            <CharacterCount>
              {currentText}/{maxLength}
            </CharacterCount>
            {inputRight}
          </InputContainer>
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

export function PasswordFormField({
  control,
  name,
  required,
  error,
  inline,
  label,
  hint,
  width,
  placeholder,
  fieldClass,
  defaultValue = '',
  ...rest
}) {
  const id = useId();
  const [showPassword, setShowPassword] = useState(false);
  const { className, disabled, ...inputProps } = rest;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField
          width={width}
          inline={inline || false}
          className={fieldClass}
        >
          <FieldLabel label={label} required={required} htmlFor={id} />
          <InputContainer inline={inline}>
            <input
              id={id}
              type={showPassword ? 'text' : 'password'}
              aria-invalid={!!error || undefined}
              placeholder={placeholder || (isValidElement(label) ? '' : label)}
              onChange={e => {
                controlOnChange(e.target.value);
              }}
              data-field-value={name}
              value={value}
              disabled={disabled}
              {...domProps(inputProps)}
              className={inputClass(error, 'sk:pr-8!', className)}
            />
            <Button
              variant="ghost"
              size="sm"
              shape="circle"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              isDisabled={disabled}
              onPress={() => setShowPassword(shown => !shown)}
              className="sk:absolute sk:top-1/2 sk:right-0.5 sk:-translate-y-1/2"
            >
              {showPassword ? (
                <EyeSlashIcon size={14} />
              ) : (
                <EyeIcon size={14} />
              )}
            </Button>
          </InputContainer>
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

export function NumberFormField({
  control,
  name,
  defaultValue,
  required,
  error,
  label,
  hint,
  width,
  placeholder,
  inputRight,
  onChange,
  helpText,
  fieldClass,
  helpPosition = 'top center',
  ...rest
}: ControlledTextFormFieldProps) {
  const id = useId();
  const { className, ...inputProps } = rest;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField width={width} className={fieldClass}>
          <FieldLabel
            label={label}
            required={required}
            helpText={helpText}
            helpPosition={helpPosition}
            htmlFor={id}
          />
          <InputContainer>
            <input
              id={id}
              type="number"
              data-field-value={name}
              aria-invalid={!!error || undefined}
              placeholder={placeholder ?? labelPlaceholder(label)}
              step="any"
              onChange={e => {
                controlOnChange(e.target.value);
                if (onChange) {
                  onChange(e.target.value);
                }
              }}
              value={value}
              {...domProps(inputProps)}
              className={inputClass(error, className)}
            />
            {inputRight}
          </InputContainer>
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

export const TextAreaFormField = forwardRef(
  (
    {
      required,
      error,
      label,
      hint,
      width,
      placeholder,
      inputRight,
      fieldClass,
      className,
      ...rest
    }: TextFormFieldProps,
    ref: React.Ref<HTMLTextAreaElement>,
  ) => {
    const id = useId();
    return (
      <CustomField width={width} className={fieldClass}>
        <FieldLabel label={label} required={required} htmlFor={id} />
        <InputContainer>
          <textarea
            id={id}
            ref={ref}
            aria-invalid={!!error || undefined}
            placeholder={placeholder ?? labelPlaceholder(label)}
            autoComplete="none"
            {...domProps(rest)}
            className={cn(
              FIELD_TEXTAREA,
              !!error && FIELD_INPUT_INVALID,
              className,
            )}
          />
          {inputRight}
        </InputContainer>
        {hint && <FieldMessage className="hint">{hint}</FieldMessage>}
      </CustomField>
    );
  },
);

export function ControlledTextAreaFormField({
  required,
  control,
  defaultValue = '',
  name,
  error,
  label,
  hint,
  width,
  placeholder,
  inputRight,
  fieldClass,
  ...rest
}: ControlledTextAreaFormFieldProps) {
  const id = useId();
  const { className, ...textareaProps } = rest;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField width={width} className={fieldClass}>
          <FieldLabel label={label} required={required} htmlFor={id} />
          <InputContainer>
            <textarea
              id={id}
              aria-invalid={!!error || undefined}
              placeholder={placeholder ?? labelPlaceholder(label)}
              autoComplete="none"
              data-field-value={name}
              value={value}
              {...domProps(textareaProps)}
              className={cn(
                FIELD_TEXTAREA,
                !!error && FIELD_INPUT_INVALID,
                className,
              )}
              onChange={e => controlOnChange(e.target.value)}
            />
            {inputRight}
          </InputContainer>
          {hint && <FieldMessage className="hint">{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

export function RTEFormField({
  required,
  error: _error,
  label,
  control,
  name,
  defaultValue,
  width,
  fieldClass,
  ...props
}: ControlledTextFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onBlur, onChange, value } }) => (
        <CustomField width={width} className={fieldClass}>
          <FieldLabel label={label} required={required} />
          <RichTextEditor
            value={value}
            data-field-value={name}
            onChange={onChange}
            onBlur={onBlur}
            {...props}
          />
        </CustomField>
      )}
    />
  );
}
